import { Badge, EmptyState, Modal } from '../../components/Ui';
import { useInventoryMovements } from '../../hooks/businessData';
import type { InventoryMovement, InventoryMovementType } from '../../../shared/types';

const movementTypeLabels: Record<InventoryMovementType, string> = {
  sale: 'Venta',
  adjustment: 'Ajuste',
  external_sync: 'Importación externa',
  reset: 'Reset diario',
  soldout_signal: 'Señal agotado',
  cancellation: 'Cancelación',
};

function formatDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('es', { dateStyle: 'short', timeStyle: 'short' }).format(date);
}

function quantityTone(quantity: number): 'green' | 'red' | 'slate' {
  if (quantity > 0) return 'green';
  if (quantity < 0) return 'red';
  return 'slate';
}

function signedQuantity(quantity: number) {
  return quantity > 0 ? `+${quantity}` : String(quantity);
}

function MovementMetadata({ movement }: { movement: InventoryMovement }) {
  const details = [
    movement.notes ? `Nota: ${movement.notes}` : null,
    movement.orderId ? `Pedido: ${movement.orderId}` : null,
  ].filter(Boolean);
  if (details.length === 0) return null;
  return <div className="mt-1 text-xs text-[var(--app-brand-muted)]">{details.join(' · ')}</div>;
}

export function InventoryMovementModal({
  branchId,
  productId,
  productName,
  open,
  onOpenChange,
}: {
  branchId: string;
  productId: string;
  productName: string;
  open: boolean;
  onOpenChange(open: boolean): void;
}) {
  const { data: movements = [], isLoading, isError } = useInventoryMovements(branchId, productId);

  return (
    <Modal open={open} onClose={() => onOpenChange(false)} title={`Historial de stock · ${productName}`} width="max-w-3xl">
      {isLoading ? (
        <div className="py-8 text-sm font-bold text-[var(--app-brand-muted)]">Cargando movimientos…</div>
      ) : isError ? (
        <EmptyState title="No se pudo cargar el historial" description="Volvé a intentar en unos segundos." />
      ) : movements.length === 0 ? (
        <EmptyState title="Sin movimientos" description="Todavía no hay ajustes, ventas ni importaciones para este producto en la sucursal." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[620px] text-left text-sm">
            <thead className="text-xs uppercase tracking-wide text-[var(--app-brand-muted)]">
              <tr>
                <th className="py-2">Fecha</th>
                <th className="py-2">Tipo</th>
                <th className="py-2 text-right">Cantidad</th>
                <th className="py-2">Detalle</th>
              </tr>
            </thead>
            <tbody>
              {movements.map((movement) => (
                <tr key={movement.id} className="border-t border-[var(--app-brand-line)] align-top">
                  <td className="py-3 font-semibold text-[var(--app-brand-ink)]">{formatDate(movement.createdAt)}</td>
                  <td className="py-3">{movementTypeLabels[movement.type]}</td>
                  <td className="py-3 text-right"><Badge tone={quantityTone(movement.quantity)}>{signedQuantity(movement.quantity)}</Badge></td>
                  <td className="py-3"><MovementMetadata movement={movement} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
